/**
 * Contract Catalog Service
 * Handles API calls for contract-related catalogs (statuses, types, modalities)
 */

import { apiClient } from '@/api/core/apiClient.ts';
import { API_CONFIG } from '@/api/core/apiConfig.ts';
import type { ApiResponse } from '@/api/shared/types.ts';
import type {
  LC_GetContractStatusOut,
  LC_GetContractTypesOut,
  LC_GetContractModalitiesOut,
  ContractStatus,
  ContractType,
  ContractModality,
} from './contract.types.ts';
import {
  mapContractStatusOutToModel,
  mapContractTypeOutToModel,
  mapContractModalityOutToModel,
} from './contract.mapper.ts';


export class ContractCatalogService {
  /**
   * Get available contract statuses
   */
  async getStatuses(): Promise<ApiResponse<ContractStatus[]>> {
    const response = await apiClient.get<LC_GetContractStatusOut>(
      API_CONFIG.ENDPOINTS.CATALOG.CONTRACT_STATUS,
    );

    if (response.success && response.data) {
      return {
        ...response,
        data: response.data.map(mapContractStatusOutToModel),
      };
    }

    return { ...response, data: null };
  }

  /**
   * Get available contract types for the authenticated user's company
   */
  async getTypes(): Promise<ApiResponse<ContractType[]>> {
    const response = await apiClient.get<LC_GetContractTypesOut>(
      API_CONFIG.ENDPOINTS.CATALOG.CONTRACT_TYPES,
    );

    if (response.success && response.data) {
      return {
        ...response,
        data: response.data.map(mapContractTypeOutToModel),
      };
    }

    return { ...response, data: null };
  }

  /**
   * Get available contract modalities for the authenticated user's company
   */
  async getModalities(): Promise<ApiResponse<ContractModality[]>> {
    const response = await apiClient.get<LC_GetContractModalitiesOut>(
      API_CONFIG.ENDPOINTS.CATALOG.CONTRACT_MODALITIES,
    );

    if (response.success && response.data) {
      return {
        ...response,
        data: response.data.map(mapContractModalityOutToModel),
      };
    }

    return { ...response, data: null };
  }
}

export const contractCatalogService = new ContractCatalogService();
